import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { createContext } from 'react';
import styled from 'styled-components';

export const ShAuthContext = createContext();

const HiddenFrame = styled.div`
  display: none;
`;

const AUTH_TOKEN_STATUS_STATES = {
  loading: 'loading',
  error: 'error',
  success: 'success',
};

const AUTH_BASEURL = 'https://services.sentinel-hub.com';
const CLIENT_ID = process.env.REACT_APP_SENTINEL_HUB_CLIENT_ID;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_CAPTCHA_SITE_KEY;
const GRANT_TYPE = 'client_credentials';
const UPDATE_SECONDS_BEFORE_EXPIRY = 60;

export function ShAuthTokenProvider({ children }) {
  const [shAuthToken, setShAuthToken] = useState('');
  const [shAuthTokenStatus, setShAuthTokenStatus] = useState(AUTH_TOKEN_STATUS_STATES.loading);
  const captchaRef = useRef();
  const iFrame = useRef();

  useEffect(() => {
    window.addEventListener('message', (e) => {
      if (e.origin === AUTH_BASEURL && e.data.access_token) {
        setShAuthToken(e.data.access_token);
        setShAuthTokenStatus(AUTH_TOKEN_STATUS_STATES.success);

        const tokenExpiresIn = (e.data.expires_in - UPDATE_SECONDS_BEFORE_EXPIRY) * 1000;
        setTimeout(() => {
          captchaRef.current.executeCaptcha();
        }, tokenExpiresIn);
      }
    });
  }, []);

  const onCaptchaExecuted = (token) => {
    iFrame.current.src =
      AUTH_BASEURL +
      '/oauth/token/assisted?client_id=' +
      CLIENT_ID +
      '&redirect_uri=not_used' +
      '&response_type=token' +
      '&grant_type=' +
      GRANT_TYPE +
      '&recaptcha=' +
      token;
  };

  return (
    <>
      <HiddenFrame>
        <iframe className="anon-frame" ref={iFrame} title="auth">
          <Captcha
            ref={captchaRef}
            onExecute={onCaptchaExecuted}
            sitekey={RECAPTCHA_SITE_KEY}
            action="token_assisted_anonymous"
          />
        </iframe>
      </HiddenFrame>
      <ShAuthContext.Provider value={{ shAuthTokenStatus, shAuthToken, AUTH_TOKEN_STATUS_STATES }}>
        {children}
      </ShAuthContext.Provider>
    </>
  );
}

function Captcha({ sitekey, action, onExecute }, ref) {
  const instance = useRef();

  useImperativeHandle(ref, () => ({
    executeCaptcha,
  }));

  useEffect(() => {
    loadCaptchaScript();
    // eslint-disable-next-line
  }, []);

  const loadCaptchaScript = () => {
    if (!window.grecaptcha) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.onload = (e) => {
        executeCaptcha();
      };
      script.src = 'https://www.google.com/recaptcha/api.js?render=' + sitekey;
      instance.current.appendChild(script);
    }
  };

  const executeCaptcha = () => {
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(sitekey, {
          action: action,
        })
        .then((captchaToken) => {
          onExecute(captchaToken);
        });
    });
  };

  return <div ref={instance} />;
}
// eslint-disable-next-line
Captcha = forwardRef(Captcha);
